import { CheckCheck, Icon, Logo, Ticket } from "@norican/icons";
import { Badge, Button } from "@norican/react";
import { useRef } from "react";
import { Link } from "react-router";

import { useMyAccessCases, useMyApprovalsNav } from "@/features/access/api";
import { DemoPersonaBanner } from "@/features/demo-persona";
import { ViewAsBanner } from "@/features/view-as";
import { useHeightVariable } from "@/hooks";
import { useT } from "@/i18n";
import { useKiosk, useMe } from "@/lib/api";

import { EnvironmentBanner, EnvironmentTag } from "./environment-banner";
import { KioskBanner } from "./kiosk-banner";
import { LanguageSwitcher } from "./language-switcher";
import { MobileNav } from "./mobile-nav";
import { PrimaryNav } from "./primary-nav";
import { UserMenu } from "./user-menu";

/**
 * The application header: brand, navigation, language, identity.
 *
 * Sticky so navigation stays reachable on a long ticket, and a real `<header>`
 * containing a real `<nav>` so the browser's landmark navigation works. Below
 * `md` the horizontal nav is replaced by the drawer trigger; nothing is hidden
 * without a replacement. On any tenant but LIVE it also carries the
 * environment banner and a tier tag beside the logo.
 *
 * Its height, banners included, is kept in `--app-header-height` (12 Oct) so
 * a page's own sticky bar can sit just below it instead of under it.
 */
export function AppHeader() {
  const { t } = useT();
  // A shared device has no list of its own to show (1 Oct).
  const kiosk = useKiosk();
  const actions = useMyActionCount(kiosk === null);
  const approvals = useMyApprovalsNav();
  const ref = useRef<HTMLElement>(null);
  useHeightVariable(ref, "--app-header-height");

  return (
    <header
      ref={ref}
      className="bg-bg/95 border-border sticky top-0 z-[var(--norican-z-sticky)] w-full border-b backdrop-blur"
    >
      {/* "View as" (6 Oct): above everything, on every page, while it is on. */}
      <ViewAsBanner />
      {/* Demo personas (19 Oct): this tab's role, in its own colour. */}
      <DemoPersonaBanner />
      <EnvironmentBanner />
      <KioskBanner />
      <div className="mx-auto flex h-16 w-full max-w-[var(--norican-size-container-xl)] min-w-0 items-center gap-2 px-3 sm:gap-3 sm:px-6 lg:px-8">
        <MobileNav className="md:hidden" />

        <Link
          to="/ask"
          aria-label={t("app.name")}
          className="flex min-w-0 shrink items-center gap-3 rounded-md"
        >
          {/* Logo only. The product name sits in the page's own eyebrow a few
              rems below, and carrying it here as well put "IT HelpDesk" on
              screen twice, stacked, which read as a mistake rather than as
              branding. `aria-label` on the link keeps the name available to
              assistive technology. */}
          {/* The NORICAN wordmark from the design system (no strapline: the
              2026 primary logo isn't in @norican/icons yet). Blue on the light
              theme, white on the dark one; decorative, the link has the name.
              The spans switch them: <Logo> sets its own inline display. */}
          <span className="flex shrink-0 [[data-theme=dark]_&]:hidden">
            <Logo brand="norican" label="" height="1.25rem" />
          </span>
          <span className="hidden shrink-0 [[data-theme=dark]_&]:flex">
            <Logo brand="norican" tone="white" label="" height="1.25rem" />
          </span>
          <EnvironmentTag />
        </Link>

        <PrimaryNav className="ml-4 hidden md:block" />

        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
          {/* The small language picker. Not on phones (6 Oct, decision 35):
              the account menu carries Language too, and the header's right
              cluster has to fit 390px. */}
          <LanguageSwitcher compact className="max-sm:hidden" />
          {/* The one standing destination in the header, beside the user menu
              on every screen: the chat is the front door, and this is the way
              straight to the list. A real link, so it can be opened in a new
              tab. Below `sm` the label collapses and `aria-label` keeps the
              name. */}
          {kiosk === null ? (
            <Button
              render={<Link to="/tickets" />}
              variant="ghost"
              tone="neutral"
              size="sm"
              aria-label={actions > 0 ? t("nav.ticketsActions", { n: actions }) : t("nav.tickets")}
              startIcon={<Icon icon={Ticket} size="sm" />}
              // Icon-only on phones: drop the gap the (hidden) label would need
              // and square the padding, so the button is just the icon.
              className="max-sm:gap-0 max-sm:px-2"
            >
              <span className="hidden sm:inline">{t("nav.tickets")}</span>
              {actions > 0 ? (
                <Badge tone="accent" variant="solid" size="xs" aria-hidden="true">
                  {actions}
                </Badge>
              ) : null}
            </Button>
          ) : null}
          {/* "My approvals" (19 Oct, Rusty could not find it in the account
              menu): beside My tickets, for approvers only (named in the
              matrix, a test approver, or with approvals waiting or decided),
              with what waits for them. Icon-only on phones, like My tickets. */}
          {approvals.show ? (
            <Button
              render={<Link to="/approvals" />}
              variant="ghost"
              tone="neutral"
              size="sm"
              aria-label={
                approvals.waiting > 0
                  ? t("nav.approvalsWaiting", { n: approvals.waiting })
                  : t("nav.approvals")
              }
              startIcon={<Icon icon={CheckCheck} size="sm" />}
              className="max-sm:gap-0 max-sm:px-2"
            >
              <span className="hidden sm:inline">{t("nav.approvals")}</span>
              {approvals.waiting > 0 ? (
                <Badge tone="accent" variant="solid" size="xs" aria-hidden="true">
                  {approvals.waiting}
                </Badge>
              ) : null}
            </Button>
          ) : null}
          <UserMenu />
        </div>
      </div>
    </header>
  );
}

/**
 * "Action for you" on My tickets (19 Oct, target flow): how many access cases
 * have a step for the reader, e.g. the manager's CRM form for a new hire.
 * Everyone follows their cases from My tickets, so the count sits there, not
 * only in the IT Admin menu. Same query as My tickets and the IT Admin badge
 * (`/access/mine`, shared cache); 0 while the front door is off.
 */
function useMyActionCount(enabled: boolean): number {
  const { data: me } = useMe();
  const mine = useMyAccessCases(enabled && me?.access_front_door === true);
  return mine.data?.action_count ?? 0;
}
